import React, { useEffect } from "react";

import Card from "../ui/Card";
import SectionCard from "../ui/SectionCard";
import BrochureSection from "../ui/BrochureSection";
import Switch from "../ui/Switch";
import ConfirmModal from "../ui/ConfirmModal";
import Image from "../ui/Image";
import Accordion from "../ui/Accordion";

import { formatBudget } from "../../hooks/commonHooks";

import { usePropertyStore } from "../../store/propertyStore";
import {
  useDeletePropertyMutation,
  useTogglePropertyStatusMutation,
} from "../../queries/properties/usePropertiesQuery";

import BrochureExtractData from "./BrochureExtractData";

import PropertyDetails from "./PropertyDetails";

import { MapPin, Building2, Calendar, Pencil, Trash2 ,  ScrollText} from "lucide-react";

import { formatDateDDMMYYYY, priceRangeLabel } from "../../hooks/commonHooks";

import { useAuthStore } from "../../store/authStore";
import Button from "../ui/Button";

export default function PropertyCard({ property }) {
  const { role } = useAuthStore();
  const isAdmin = role === "admin";

  const { setExtractedPdfData } = usePropertyStore();

  const {
    id,
    title: propertyTitle,
    name,
    agent_id: propertyAgentId,
    agentId,
    area,
    city,
    state,
    country,
    pincode,
    property_type: propertyType,
    unit_type: unitTypes = [],
    min_budget,
    max_budget,
    price_range,
    expected_delivery,
    image_url,
    property_url,
    brochure_url,
    is_active,
    details,
  } = property;

  useEffect(() => {
    if (details && isAdmin) {
      setExtractedPdfData(details);
    }
  }, [details]);

  const title = propertyTitle || name || "";
  const agentIdToUse = propertyAgentId ?? agentId;
  const isActive = is_active;
  const imageUrl = typeof image_url === "string" ? image_url : null;

  const { mutate: toggleStatus, isPending: isTogglePending } =
    useTogglePropertyStatusMutation();

  const deleteMutation = useDeletePropertyMutation();
  const { setSelectedProperty } = usePropertyStore();

  const [isPropertyDetailsOpen, setIsPropertyDetailsOpen] = React.useState(false);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [isToggleConfirmOpen, setIsToggleConfirmOpen] = React.useState(false);
  const [isBrochureExtractDataOpen, setIsBrochureExtractDataOpen] =
    React.useState(false);

  const handleEdit = () => setSelectedProperty(property);
  const handleDelete = () => setIsDeleteConfirmOpen(true);
  const handleToggleStatus = () => setIsToggleConfirmOpen(true);

  const confirmDelete = () => {
    deleteMutation.mutate({ agentId: agentIdToUse, propertyId: id });
    setIsDeleteConfirmOpen(false);
  };

  const confirmToggle = () => {
    toggleStatus({ agentId: agentIdToUse, propertyId: id });
    setIsToggleConfirmOpen(false);
  };

  let unitTypesToDisplay;

  if (propertyType !== "plot") {
    unitTypesToDisplay = (
      <div className="unit-types mt-2">
        {(Array.isArray(unitTypes) ? unitTypes : []).map((unit) => (
          <span key={unit} className="unit-badge">
            {unit !== null ? unit : "N/A"}
          </span>
        ))}
      </div>
    );
  }

  return (
    <Card className="property-card m-0 p-0 position-relative">
      {/* ACTIONS */}
      {isAdmin && (
        <div className="property-actions">
          <button onClick={handleEdit}>
            <Pencil size={18} />
          </button>
          <button onClick={handleDelete} disabled={deleteMutation.isPending}>
            <Trash2 size={18} className="text-danger" />
          </button>
        </div>
      )}

      {/* HEADER */}
      <div className="property-card-header  d-flex flex-column gap-2">
        <div className="property-image-wrapper">
          <Image
            src={imageUrl}
            alt={title || "Property image"}
            className="property-image"
          />
        </div>
        <div className="px-2 d-flex align-items-center justify-between">
          <h3 className="property-title">{title || "Untitled Property"}</h3>
          {isAdmin && (
            <Switch
              checked={isActive}
              onChange={handleToggleStatus}
              disabled={isTogglePending}
            />
          )}
        </div>

        {/* ✅ SUMMARY (always visible) */}
        <div className="property-summary px-2 d-flex justify-between">
          <div className="property-item w-60">
            <MapPin size={16} />
            <span>
              {city || "—"}, {area || "—"}
            </span>
          </div>

          <div className="property-item gap-0">
            <span className="text-primary">
              {priceRangeLabel(
                formatBudget(min_budget),
                formatBudget(max_budget),
                price_range,
              )}
            </span>
          </div>
        </div>
      </div>

      {/* 📍 LOCATION ACCORDION */}
      <Accordion title="Location Details" className="px-2">
        <div className="property-grid">
          <div className="property-item">
            <MapPin size={16} />
            <span>Area: {area || "—"}</span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>City: {city || "—"}</span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>State: {state || "—"}</span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>Country: {country || "—"}</span>
          </div>
          <div className="property-item">
            <MapPin size={16} />
            <span>Pincode: {pincode || "—"}</span>
          </div>
        </div>
      </Accordion>

      {/* 🏢 PROPERTY DETAILS */}
      <Accordion title="Property Details" className="px-2">
        <div className="property-grid">
          <div className="property-item">
            <Building2 size={16} />
            <span>Type: {propertyType || "—"}</span>
          </div>

          <div className="property-item">
            <Calendar size={16} />
            <span>Delivery: {formatDateDDMMYYYY(expected_delivery)}</span>
          </div>
        </div>

        {unitTypesToDisplay}
      </Accordion>

      {/* 🔗 LINKS */}
      <Accordion title="Links & Documents" className="px-2">
        {property_url && (
          <div className="mb-2 overflow-hidden">
            <strong>Property URL:</strong>
            <br />
            <a href={property_url} target="_blank" rel="noreferrer">
              {property_url}
            </a>
          </div>
        )}

        {brochure_url && (
          <BrochureSection
            url={brochure_url}
            title={brochure_url?.split("/").pop()}
          />
        )}
     
      </Accordion>

      <Accordion title="Features & Amenities" className="px-2  property-details-section">
        <div className="property-details-grid">
        {isAdmin && (
          <div className="property-details-card w-100">
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="w-100"
              onClick={() => setIsBrochureExtractDataOpen(true)}
            >Update PDF Extract</Button>
          </div>
        )}
         <div className="property-details-card w-100">
            <Button
              type="button"
              variant="secondary"
              className="w-100"
              size="md"
              onClick={() => setIsPropertyDetailsOpen(true)}
            >
              View Amenities
            </Button>
          </div>
          </div>
          </Accordion>

      {/* FOOTER */}
      <div className="property-footer px-2 py-2">
        <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      </div>

      {/* MODALS */}
      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        title="Delete Property"
        message={`Are you sure you want to delete "${title}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        isLoading={deleteMutation.isPending}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={isToggleConfirmOpen}
        title={isActive ? "Deactivate Property" : "Activate Property"}
        message={
          isActive
            ? "This property will no longer be visible to leads."
            : "This property will be marked as active."
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        cancelLabel="Cancel"
        onConfirm={confirmToggle}
        onCancel={() => setIsToggleConfirmOpen(false)}
        isLoading={isTogglePending}
        confirmVariant="primary"
      />

      <BrochureExtractData
        details={details}
        isBrochureExtractDataOpen={isBrochureExtractDataOpen}
        onCloseBrochureExtractData={() => setIsBrochureExtractDataOpen(false)}
      />

      <PropertyDetails
        PropertyDetails={details}
        isOpen={isPropertyDetailsOpen}
        onClose={() => setIsPropertyDetailsOpen(false)}
      />
    </Card>
  );
}
